import React, { useState, useEffect, useRef } from "react";
import {
  Trash2,
  Plus,
  Minus,
  User,
  CreditCard,
  Truck,
  ShoppingBag,
  Users,
  Save,
  MessageSquare,
  Layers,
  UserX,
  QrCode,
  ChevronDown,
  X,
} from "lucide-react";
import UnitModal from "./UnitModal";
import { calculateOrderItemTotals } from "../../utils/salesUtils";

function Grid2x2PlusIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <path d="M14 17.5h7" />
      <path d="M17.5 14v7" />
    </svg>
  );
}

function Grid2x2XIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <path d="m15.5 15.5 5 5" />
      <path d="M20.5 15.5l-5 5" />
    </svg>
  );
}

function CartQtyInput({ value, onChange }) {
  const [localVal, setLocalVal] = useState(String(value ?? "1"));

  useEffect(() => {
    const numVal = Number(value || 0);
    const parsedLocal = parseFloat(String(localVal).replace(",", "."));
    if (isNaN(parsedLocal) || Math.abs(parsedLocal - numVal) > 0.0001) {
      setLocalVal(String(value ?? "1"));
    }
  }, [value]);

  const handleInputChange = (e) => {
    let raw = e.target.value;
    // Allow digits, comma, dot
    raw = raw.replace(/[^0-9.,]/g, "");

    // Disallow multiple dots or commas
    const separators = raw.match(/[.,]/g) || [];
    if (separators.length > 1) {
      return;
    }

    setLocalVal(raw);

    const normalized = raw.replace(",", ".");
    const parsed = parseFloat(normalized);
    if (!isNaN(parsed) && parsed > 0) {
      onChange(parsed);
    }
  };

  const handleBlur = () => {
    const normalized = String(localVal).replace(",", ".");
    const parsed = parseFloat(normalized);
    if (isNaN(parsed) || parsed <= 0) {
      setLocalVal("1");
      onChange(1);
    } else {
      const cleanVal = String(Math.round(parsed * 1000) / 1000);
      setLocalVal(cleanVal);
      onChange(Number(cleanVal));
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={localVal}
      onChange={handleInputChange}
      onBlur={handleBlur}
      onFocus={(e) => e.target.select()}
      className="w-16 h-8 text-center text-sm font-semibold text-gray-800 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
    />
  );
}

function CartItemRow({ item, originalIndex, onUpdateQty, onRemove, onUpdateItemNotes, onUpdateItemPrice, onSelectUnit }) {
  const [localNote, setLocalNote] = useState(item.notes || "");
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const priceDropdownRef = useRef(null);

  useEffect(() => {
    setLocalNote(item.notes || item.note || item.catatan || item.item_notes || "");
  }, [item.notes, item.note, item.catatan, item.item_notes]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (priceDropdownRef.current && !priceDropdownRef.current.contains(e.target)) {
        setIsPriceOpen(false);
      }
    };
    if (isPriceOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPriceOpen]);

  const handleNotesChange = (e) => {
    const val = e.target.value;
    setLocalNote(val);
    if (onUpdateItemNotes) {
      onUpdateItemNotes(originalIndex, val);
    }
  };

  const itemKey = item.cart_id || item.id || originalIndex;
  const sellingPrices = (item.selling_prices || item.product?.selling_prices || []).filter(
    (sp) => sp.status === "ACTIVE" || !sp.status
  );
  const baseMainPrice =
    item.base_sell_price !== undefined && item.base_sell_price !== null
      ? item.base_sell_price
      : (item.product?.sell_price || item.base_price || item.price || 0);
  const currentPrice = item.price !== undefined ? item.price : item.unit_price;
  const itemTotals = calculateOrderItemTotals(item);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      {/* Header: Product Name and Price */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1 pr-4 min-w-0">
          <h4 className="font-semibold text-gray-900 text-sm">
            {(() => {
              const nameStr = item.product_name || item.name || item.item_name || "";
              const suffix = item.variant_name ? ` - ${item.variant_name}` : "";
              return (suffix && nameStr.endsWith(suffix)) ? nameStr.slice(0, -suffix.length) : nameStr;
            })()}
          </h4>
          
          {item.variant_name && (
            <p className="text-xs text-slate-500 mt-0.5">
              Varian: <span className="font-medium text-slate-800">{item.variant_name}</span>
            </p>
          )}

          {item.selected_options && item.selected_options.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {item.selected_options.map((opt, oIdx) => (
                <span
                  key={oIdx}
                  className="inline-flex items-center text-[10px] bg-slate-100 text-slate-700 font-medium px-2 py-0.5 rounded-md">
                  + {opt.name} {opt.qty > 1 ? `x${opt.qty}` : ''} (Rp {(opt.price || opt.sell_price || 0).toLocaleString()})
                </span>
              ))}
            </div>
          )}

          {/* Keterangan Unit (Hanya tampil jika kasir sudah memilih unit) */}
          {(item.unit_name || item.unit?.name) && (
            <p className="text-xs text-teal-700 mt-1 inline-flex items-center gap-1 bg-teal-50 ring-1 ring-teal-200 px-2 py-0.5 rounded-full font-medium">
              <Layers className="w-3 h-3" />
              Unit: {item.unit_name || item.unit?.name}
            </p>
          )}
        </div>

        {/* Price display / Dropdown if multi-tier prices exist */}
        {sellingPrices.length > 0 ? (
          <div className="relative" ref={priceDropdownRef}>
            <button
              type="button"
              onClick={() => setIsPriceOpen((prev) => !prev)}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs sm:text-sm font-bold text-slate-800 transition-colors shadow-2xs cursor-pointer"
              title="Pilih Harga Jual"
            >
              Rp {(currentPrice || 0).toLocaleString("id-ID")}
              <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
            </button>

            {isPriceOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-60 sm:w-64 bg-white rounded-xl shadow-xl border border-slate-200 p-3 z-50 animate-in fade-in zoom-in-95 duration-150">
                <div className="text-xs font-bold text-slate-800 mb-2">
                  Pilih Harga Jual
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {/* Harga Utama */}
                  <label
                    onClick={() => {
                      if (onUpdateItemPrice) {
                        onUpdateItemPrice(originalIndex, baseMainPrice, null);
                      }
                      setIsPriceOpen(false);
                    }}
                    className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                      currentPrice === baseMainPrice && !item.selected_price_id
                        ? "bg-teal-50/70 border border-teal-200"
                        : "hover:bg-slate-50 border border-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={`cart_item_price_${itemKey}`}
                        checked={currentPrice === baseMainPrice && !item.selected_price_id}
                        onChange={() => {}}
                        className="text-teal-600 focus:ring-teal-500 w-4 h-4 cursor-pointer"
                      />
                      <span className="text-xs font-bold text-slate-800">
                        Rp {baseMainPrice.toLocaleString("id-ID")}
                      </span>
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded font-bold border border-emerald-200">
                      Utama
                    </span>
                  </label>

                  {/* Varian Harga Jual Tambahan (Grosir, dll) */}
                  {sellingPrices.map((sp) => {
                    const isSelected =
                      item.selected_price_id === sp.id ||
                      (currentPrice === Number(sp.price) && item.selected_price_name === sp.name);
                    return (
                      <label
                        key={sp.id}
                        onClick={() => {
                          if (onUpdateItemPrice) {
                            onUpdateItemPrice(originalIndex, Number(sp.price), sp);
                          }
                          setIsPriceOpen(false);
                        }}
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? "bg-teal-50/70 border border-teal-200"
                            : "hover:bg-slate-50 border border-transparent"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`cart_item_price_${itemKey}`}
                            checked={isSelected}
                            onChange={() => {}}
                            className="text-teal-600 focus:ring-teal-500 w-4 h-4 cursor-pointer"
                          />
                          <span className="text-xs font-bold text-slate-800">
                            Rp {Number(sp.price).toLocaleString("id-ID")}
                          </span>
                        </div>
                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded font-bold border border-slate-200">
                          {sp.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-400 font-medium">
                  Harga berlaku untuk produk ini saja
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-700 text-base font-medium whitespace-nowrap">
            Rp {(currentPrice || 0).toLocaleString("id-ID")}
          </p>
        )}
      </div>

      {/* Total and Quantity Controls */}
      <div className="flex justify-between items-center mb-3">
        <div>
          <p className="text-gray-500 text-sm">
            Total:{" "}
            <span className="font-semibold text-gray-800">
              Rp{" "}
              {itemTotals.subtotal.toLocaleString("id-ID")}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onUpdateQty(itemKey, -1)}
            className="w-8 h-8 flex items-center justify-center text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
            <Minus className="w-4 h-4" />
          </button>
          <CartQtyInput
            value={item.quantity || item.qty || 1}
            onChange={(newVal) => onUpdateQty(itemKey, newVal, true)}
          />
          <button
            onClick={() => onUpdateQty(itemKey, 1)}
            className="w-8 h-8 flex items-center justify-center text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Action Buttons and Notes */}
      <div className="flex gap-2">
        <button
          onClick={() => onRemove(itemKey)}
          className="p-2.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          title="Hapus Produk">
          <Trash2 className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => onSelectUnit && onSelectUnit(item)}
          className={`hidden p-2.5 text-white rounded-md transition-colors ${
            (item.unit_name || item.unit?.name)
              ? "bg-emerald-600 hover:bg-emerald-700"
              : "bg-teal-600 hover:bg-teal-700"
          }`}
          title={
            (item.unit_name || item.unit?.name)
              ? `Unit terpilih: ${item.unit_name || item.unit?.name}`
              : "Pilih unit"
          }>
          <Layers className="w-5 h-5" />
        </button>
        <input
          type="text"
          placeholder="Masukan catatan bila diperlukan..."
          value={localNote}
          onChange={handleNotesChange}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        />
      </div>
    </div>
  );
}

export default function CartSidebar({
  cart,
  customer,
  salesType,
  walkInName = "",
  onWalkInNameChange,
  selectedTable = null,
  onSelectTable,
  onUnselectTable,
  onSelectCustomer,
  onClearCustomer,
  isCustomerQREnabled = false,
  onCustomerQR,
  onSelectUnit,
  selectedUnit = null,
  onSelectTransactionUnit,
  onClearTransactionUnit,
  onChangeSalesType,
  onRemove,
  onUpdateQty,
  onUpdateItemNotes,
  onUpdateItemPrice,
  onClear,
  onPay,
  onSaveDraft,
  isSavingDraft,
  subtotal,
  total,
  additionalCosts,
  discountTotal,
  dpAmount = 0,
}) {
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [selectedItemForUnit, setSelectedItemForUnit] = useState(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);

  const handleSelectUnit = (item, idx) => {
    setSelectedItemForUnit(item);
    setSelectedItemIndex(typeof idx === "number" ? idx : null);
    setShowUnitModal(true);
  };

  const handleOpenTransactionUnitModal = () => {
    setSelectedItemForUnit(null);
    setSelectedItemIndex(null);
    setShowUnitModal(true);
  };

  const handleUnitSelected = (unit) => {
    if (selectedItemForUnit || typeof selectedItemIndex === "number") {
      if (onSelectUnit) {
        if (typeof selectedItemIndex === "number") {
          onSelectUnit(selectedItemIndex, unit);
        } else if (selectedItemForUnit) {
          onSelectUnit(selectedItemForUnit.id || selectedItemForUnit.cart_id, unit);
        }
      }
    } else {
      if (onSelectTransactionUnit) {
        onSelectTransactionUnit(unit);
      }
    }
    setShowUnitModal(false);
    setSelectedItemForUnit(null);
    setSelectedItemIndex(null);
  };

  const currentUnitForModal =
    selectedItemForUnit?.unit ||
    (selectedItemForUnit?.unit_id || selectedItemForUnit?.unit_name
      ? {
          id: selectedItemForUnit?.unit_id || null,
          name: selectedItemForUnit?.unit_name || null,
        }
      : selectedUnit || null);
  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";

  return (
    <div className="flex flex-col h-full bg-gray-50 relative">
      {/* Header Section */}
      <div className="p-4 border-b bg-white">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-base truncate">
              {customer ? customer.name : "Pelanggan Umum"}
            </h3>
            {customer ? (
              <p className="text-xs text-gray-500 font-medium mt-0.5">
                {isApps && selectedTable ? (
                  <span className="text-teal-700 font-bold">{selectedTable.name}</span>
                ) : (
                  <span>Pelanggan Terdaftar</span>
                )}
              </p>
            ) : (
              <div className="flex flex-col">
                <input
                  type="text"
                  value={walkInName}
                  onChange={(e) => onWalkInNameChange?.(e.target.value)}
                  placeholder="Ketikan nama pelanggan disini (opsional, hanya untuk nota)"
                  className="mt-1 w-full text-xs text-gray-700 placeholder:text-gray-400 bg-transparent border-b border-dashed border-gray-300 focus:border-teal-500 focus:outline-none py-0.5"
                />
                {isApps && selectedTable && (
                  <p className="text-xs text-teal-700 font-bold mt-1">
                    {selectedTable.name}
                  </p>
                )}
              </div>
            )}

            {/* Badge Unit Kendaraan Terpilih */}
            {selectedUnit && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <button
                  type="button"
                  onClick={handleOpenTransactionUnitModal}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-teal-50 border border-teal-200 text-xs text-teal-800 font-semibold hover:bg-teal-100 transition-colors"
                  title="Klik untuk mengganti unit kendaraan">
                  <Truck className="w-3.5 h-3.5 text-teal-600" />
                  <span className="truncate max-w-[140px]">Unit: {selectedUnit.name || selectedUnit.unit_name}</span>
                </button>
                {onClearTransactionUnit && (
                  <button
                    type="button"
                    onClick={onClearTransactionUnit}
                    className="p-0.5 text-gray-400 hover:text-rose-600 rounded transition-colors"
                    title="Hapus unit transaksi">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 ml-2">
            {/* 0. Icon QR Pelanggan E-Catalog (di sebelah kiri icon pelanggan, icon saja tanpa text) */}
            {isCustomerQREnabled && onCustomerQR && (
              <button
                type="button"
                onClick={onCustomerQR}
                className="p-2 rounded-xl bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200 transition-all flex items-center justify-center shadow-sm"
                title="Cetak QR Pelanggan E-Catalog">
                <QrCode className="w-5 h-5" />
              </button>
            )}

            {/* 1. Icon Pelanggan (Users / UserX bila dipilih) */}
            {customer ? (
              <button
                onClick={onClearCustomer}
                className="p-2 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200 transition-all flex items-center justify-center shadow-sm"
                title="Jadikan Pelanggan Umum">
                <UserX className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={onSelectCustomer}
                className="p-2 rounded-xl bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 transition-all flex items-center justify-center shadow-sm"
                title="Pilih Pelanggan">
                <Users className="w-5 h-5" />
              </button>
            )}

            {/* 1.5 Icon Unit Kendaraan (di samping icon Pelanggan) */}
            {selectedUnit ? (
              <button
                type="button"
                onClick={handleOpenTransactionUnitModal}
                className="p-2 rounded-xl bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200 transition-all flex items-center justify-center shadow-sm"
                title={`Unit Kendaraan: ${selectedUnit.name || selectedUnit.unit_name} (Klik untuk ganti)`}>
                <Truck className="w-5 h-5 text-teal-600" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleOpenTransactionUnitModal}
                className="p-2 rounded-xl bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 transition-all flex items-center justify-center shadow-sm"
                title="Pilih Unit Kendaraan">
                <Truck className="w-5 h-5" />
              </button>
            )}

            {/* 2. Icon Meja (Hanya tampil pada Mode Kuliner / Apps) */}
            {isApps &&
              (selectedTable ? (
                <button
                  onClick={onUnselectTable}
                  className="p-2 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200 transition-all flex items-center justify-center shadow-sm"
                  title={`Batalkan Meja ${selectedTable.name}`}>
                  <Grid2x2XIcon className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={onSelectTable}
                  className="p-2 rounded-xl bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 transition-all flex items-center justify-center shadow-sm"
                  title="Pilih Meja">
                  <Grid2x2PlusIcon className="w-5 h-5" />
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* Cart Items / Empty State */}
      <div className="flex-1 overflow-y-auto p-4">
        {cart.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-32 h-32 mx-auto mb-4 opacity-50">
              {/* Illustration Placeholder */}
              <svg
                viewBox="0 0 200 200"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full text-gray-200">
                <path
                  d="M100 180C144.183 180 180 144.183 180 100C180 55.8172 144.183 20 100 20C55.8172 20 20 55.8172 20 100C20 144.183 55.8172 180 100 180Z"
                  fill="#F3F4F6"
                />
                <path
                  d="M65 80H135V140H65V80Z"
                  fill="white"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                />
                <path
                  d="M85 80V60C85 51.7157 91.7157 45 100 45C108.284 45 115 51.7157 115 60V80"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                />
                <circle
                  cx="120"
                  cy="120"
                  r="15"
                  fill="white"
                  stroke="#3B82F6"
                  strokeWidth="2"
                />
                <path d="M115 115L125 125" stroke="#3B82F6" strokeWidth="2" />
                <path d="M125 115L115 125" stroke="#3B82F6" strokeWidth="2" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-700 text-sm mb-1">
              Belum Ada Produk
            </h3>
            <p className="text-gray-400 text-xs max-w-[180px]">
              Pilih produk dari katalog untuk memulai transaksi
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {cart
              .map((item, originalIndex) => ({ item, originalIndex }))
              .sort((a, b) => {
                // Baik Mode Kuliner maupun Mode Retail: Berdasarkan urutan produk dipilih/diklik (insertion order dari atas ke bawah)
                return a.originalIndex - b.originalIndex;
              })
              .map(({ item, originalIndex }) => (
                <CartItemRow
                  key={item.cart_id || item.id || `${item.product_id || item.service_id || 'item'}-${item.product_variant_id || 'base'}-${originalIndex}`}
                  item={item}
                  originalIndex={originalIndex}
                  onUpdateQty={onUpdateQty}
                  onRemove={onRemove}
                  onUpdateItemNotes={onUpdateItemNotes}
                  onUpdateItemPrice={onUpdateItemPrice}
                  onSelectUnit={(itemToSelect) => handleSelectUnit(itemToSelect, originalIndex)}
                />
              ))}
          </div>
        )}
      </div>

      {/* Unit Selection Modal */}
      <UnitModal
        isOpen={showUnitModal}
        onClose={() => {
          setShowUnitModal(false);
          setSelectedItemForUnit(null);
        }}
        onSelect={handleUnitSelected}
        currentUnit={currentUnitForModal}
      />

      {/* Bottom Panel */}
      <div className="p-4 bg-white border-t">
        {/* Sales Type */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-xs text-gray-500">Tipe Penjualan :</p>
            <p className="font-bold text-gray-900 text">
              {salesType?.name || salesType || "Belum dipilih"}
            </p>
          </div>
          <button
            onClick={onChangeSalesType}
            className="px-5 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors">
            Ubah
          </button>
        </div>

        {/* Separator */}
        <div className="border-t border-gray-200 mb-3"></div>

        {/* Subtotal & Discount / Markup */}
        <div className="mb-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-500 font-normal text-base">Subtotal</span>
            {/* [HIDDEN_SALE_TYPE_DISCOUNT_MARKUP]: Original strikethrough subtotal when discount/markup active:
            {salesType?.jenis === 'Diskon' || salesType?.jenis === 'Markup' ? (
              <span className="text-sm font-medium text-gray-400 line-through">
                Rp {subtotal?.toLocaleString() || "0"}
              </span>
            ) : (
              <span className="text-base font-bold text-gray-900">
                Rp {subtotal?.toLocaleString() || "0"}
              </span>
            )} */}
            <span className="text-base font-bold text-gray-900">
              Rp {total?.toLocaleString() || "0"}
            </span>
          </div>
          
          {/* [HIDDEN_SALE_TYPE_DISCOUNT_MARKUP]: Original Diskon badge row:
          {salesType?.jenis === 'Diskon' && discountTotal > 0 && (
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs font-semibold text-rose-600 bg-rose-50 px-2 py-1 rounded-md border border-rose-100 flex items-center gap-1">
                Diskon {salesType.satuan === '%' ? `${salesType.nilai}%` : `Rp ${Number(salesType.nilai).toLocaleString()}`}
              </span>
              <span className="text-lg font-bold text-gray-900">
                Rp {total?.toLocaleString() || "0"}
              </span>
            </div>
          )} */}
          
          {/* [HIDDEN_SALE_TYPE_DISCOUNT_MARKUP]: Original Markup badge row:
          {salesType?.jenis === 'Markup' && additionalCosts > 0 && (
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-md border border-blue-100 flex items-center gap-1">
                Markup {salesType.satuan === '%' ? `${salesType.nilai}%` : `Rp ${Number(salesType.nilai).toLocaleString()}`}
              </span>
              <span className="text-lg font-bold text-gray-900">
                Rp {total?.toLocaleString() || "0"}
              </span>
            </div>
          )} */}

          {dpAmount > 0 && (
            <div className="mt-3 pt-3 border-t border-dashed border-gray-200 space-y-1">
              <div className="flex justify-between items-center text-xs text-amber-700">
                <span>DP (Telah Dibayar)</span>
                <span className="font-semibold">- Rp {dpAmount.toLocaleString("id-ID")}</span>
              </div>
              <div className="flex justify-between items-center text-sm font-bold text-teal-700 pt-1">
                <span>Sisa Tagihan</span>
                <span>Rp {Math.max(0, total - dpAmount).toLocaleString("id-ID")}</span>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onPay}
            disabled={cart.length === 0}
            className="flex-1 py-3 bg-black text-white rounded-lg font-bold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base">
            Proses Pembayaran
          </button>

          <button
            onClick={onSaveDraft}
            disabled={cart.length === 0 || isSavingDraft}
            title="Simpan sebagai draft pending"
            className="p-3 border border-orange-300 bg-orange-50 text-orange-500 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Save className="w-5 h-5" />
          </button>

          <button
            onClick={onClear}
            disabled={cart.length === 0}
            className="p-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50">
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
